import { AuthProvider, OAuthActionType, OAuthSuccessResponse } from '@avanzu/types';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { apiClient } from '../../../services';
import ProviderCard from './ProviderCard';

interface XeroAccountLinkProps {
  onSuccess: (message: string, provider: AuthProvider) => void;
  onError: (error: string, provider: AuthProvider) => void;
  onClearError: () => void;
}

const XeroAccountLink: React.FC<XeroAccountLinkProps> = ({ onError, onClearError, onSuccess }) => {
  const { user, refreshUserOrganisationState } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const [hasProcessedMessage, setHasProcessedMessage] = useState(false);

  // Wrap the callback functions to avoid recreating them
  const handleOAuthError = useCallback(
    (error: string, provider: AuthProvider) => {
      onError(error, provider);
    },
    [onError]
  );
  const handleSuccess = useCallback(
    (message: string, provider: AuthProvider) => {
      onSuccess(message, provider);
    },
    [onSuccess]
  );
  const handleClearError = useCallback(() => {
    onClearError();
  }, [onClearError]);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (hasProcessedMessage) return;

      const { success, state, code, provider, action } = event.data as OAuthSuccessResponse;
      if (action !== OAuthActionType.LINK) return;
      if (provider !== AuthProvider.XERO) return;
      if (!success || typeof success === 'undefined') return;

      if (success) {
        // Handle OAuth success for linking
        if (code && state) {
          setHasProcessedMessage(true);
          try {
            // Call the link endpoint with the OAuth code
            const response = await apiClient.post('/auth/oauth/xero/link', {
              code: code,
              state: state,
            });

            if (response.ok) {
              handleSuccess('Xero account connected successfully', AuthProvider.XERO);
              setIsConnecting(false);
              await refreshUserOrganisationState();
            } else {
              const errorData = await response.json();
              setIsConnecting(false);
              handleOAuthError(
                errorData.message || 'Failed to link Xero account. Please try again.',
                AuthProvider.XERO
              );
            }
          } catch (error) {
            console.error('Failed to link Xero account:', error);
            handleOAuthError('Failed to link Xero account. Please try again.', AuthProvider.XERO);
            setIsConnecting(false);
          }
        }
        // // Handle OAuth success for regular login
        // else if (
        //   successData.provider === AuthProvider.XERO &&
        //   (successData.action === OAuthActionType.LOGIN ||
        //     successData.action === OAuthActionType.SIGNUP) &&
        //   successData.token
        // ) {
        //   setHasProcessedMessage(true);
        //   try {
        //     setIsConnecting(false);
        //     await refreshUserOrganisationState();
        //   } catch (error) {
        //     console.error('Failed to refresh user data:', error);
        //     handleOAuthError(
        //       'Account connected but failed to refresh. Please reload the page.',
        //       AuthProvider.XERO
        //     );
        //     setIsConnecting(false);
        //   }
        // }
      } else {
        // Handle OAuth error
        setHasProcessedMessage(true);
        console.error('Xero OAuth error:', event.data.message);
        handleOAuthError(
          event.data.message || 'Failed to connect Xero account. Please try again.',
          AuthProvider.XERO
        );
        setIsConnecting(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    handleOAuthError,
    handleSuccess,
    handleClearError,
    hasProcessedMessage,
    refreshUserOrganisationState,
  ]);

  const handleConnect = () => {
    setIsConnecting(true);
    setHasProcessedMessage(false); // Reset the processed message state
    handleClearError();

    const width = 500;
    const height = 800;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `${process.env.REACT_APP_API_URL}/auth/oauth/xero?popup=true&action=${OAuthActionType.LINK}&returnUrl=/security`,
      'xero-oauth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );

    if (!popup || popup.closed) {
      handleOAuthError(
        'Please allow popups for this site to connect your Xero account.',
        AuthProvider.XERO
      );
      setIsConnecting(false);
      return;
    }

    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        setTimeout(() => {
          if (isConnecting && !hasProcessedMessage) {
            handleOAuthError(
              'Connection cancelled or failed. Please try again.',
              AuthProvider.XERO
            );
            setIsConnecting(false);
          }
        }, 500);
      }
    }, 1000);
  };

  const handleDisconnect = async () => {
    try {
      const response = await apiClient.post('/auth/oauth/xero/disconnect');

      if (response.ok) {
        await refreshUserOrganisationState();
      } else {
        const errorData = await response.json();
        const errorMessage =
          errorData?.message || 'Failed to disconnect Xero account. Please try again.';
        handleOAuthError(errorMessage, AuthProvider.XERO);
      }
    } catch (error) {
      console.error('Error disconnecting Xero account:', error);
      handleOAuthError('Failed to disconnect Xero account. Please try again.', AuthProvider.XERO);
    }
  };

  return (
    <ProviderCard
      email={user?.email}
      provider={AuthProvider.XERO}
      isConnected={user?.providers.includes(AuthProvider.XERO) || false}
      isConnecting={isConnecting}
      onConnect={handleConnect}
      onDisconnect={handleDisconnect}
    />
  );
};

export default XeroAccountLink;
